function capitalise(text) {
  return text[0].toUpperCase() + text.slice(1);
}
const UC_RE = /[A-Z]/;
function splitStringByCase(text) {
  let output = "";
  let index = 0;
  for (const char of text) {
    const lastChar = index ? text[index - 1] : "";
    const nextChar = text[index + 1];
    if (UC_RE.test(char) && (!UC_RE.test(lastChar) || nextChar && !UC_RE.test(nextChar))) {
      output += " ";
    }
    output += char;
    index++;
  }
  return capitalise(output);
}
function inArray(items) {
  return Array.isArray(items) ? items : [items];
}
function quoteIfString(potentialString) {
  return typeof potentialString === "string" ? `"${potentialString}"` : `${potentialString}`;
}
const createProxy = (path) => new Proxy(path, {
  get(target, prop) {
    switch (prop) {
      case "use":
        return (defaultValue) => {
          const path2 = target.join(".").replace(/\.->/g, "->").replace(/\.\[\]/g, "[]");
          if (defaultValue === void 0)
            return path2;
          return `coalesce(${path2},${quoteIfString(defaultValue)})`;
        };
      case "resolveIn":
        return (attr) => {
          const wrappedAttributes = Array.isArray(attr) ? `{${attr.join(", ")}}` : attr;
          return createProxy([...target, `[]->${wrappedAttributes}`]);
        };
      case "resolve":
        return (attr) => {
          const wrappedAttributes = Array.isArray(attr) ? `{${attr.join(", ")}}` : attr;
          return createProxy([...target, `->${wrappedAttributes}`]);
        };
      case "pick":
        return (attr) => {
          const wrappedAttributes = Array.isArray(attr) ? `{${attr.join(", ")}}` : `.${attr}`;
          return createProxy([...target, `[]${wrappedAttributes}`]);
        };
      case "count":
        return () => `count(${path})`;
      default:
        return createProxy([...target, prop]);
    }
  }
});

class QueryBuilder {
  constructor(schema, ordering = [], projections = {}, mappings = {}, subqueries = {}, selector = "", project = true, restricted = false, filters = []) {
    this.schema = schema;
    this.projections = projections;
    this.mappings = mappings;
    this.subqueries = subqueries;
    this.selector = selector;
    this.project = project;
    this.ordering = ordering;
    this.restricted = restricted;
    this.filters = filters;
  }
  orderBy(key, order = "asc") {
    return new QueryBuilder(
      this.schema,
      [...this.ordering, [key, order]],
      this.projections,
      this.mappings,
      this.subqueries,
      this.selector,
      this.project,
      this.restricted,
      this.filters
    );
  }
  select(from, to, exclusive = false) {
    return new QueryBuilder(
      this.schema,
      this.ordering,
      this.projections,
      this.mappings,
      this.subqueries,
      ` [${from}..${exclusive ? "." : ""}${to}]`,
      this.project,
      this.restricted,
      this.filters
    );
  }
  // eslint-disable-next-line no-dupe-class-members
  pick(props) {
    const project = Array.isArray(props);
    const projections = inArray(props).reduce((obj, key) => {
      obj[key] = key;
      return obj;
    }, {});
    return new QueryBuilder(
      this.schema,
      this.ordering,
      projections,
      this.mappings,
      this.subqueries,
      this.selector,
      project,
      true,
      this.filters
    );
  }
  first() {
    return new QueryBuilder(
      this.schema,
      this.ordering,
      this.projections,
      this.mappings,
      this.subqueries,
      " [0]",
      this.project,
      this.restricted,
      this.filters
    );
  }
  map(map) {
    let mappings;
    function checkCallable(m) {
      return typeof m === "function";
    }
    if (checkCallable(map)) {
      mappings = map(createProxy([]));
    } else {
      mappings = map;
    }
    return new QueryBuilder(
      this.schema,
      this.ordering,
      this.projections,
      mappings,
      this.subqueries,
      this.selector,
      this.project,
      this.restricted,
      this.filters
    );
  }
  subquery(subqueries) {
    return new QueryBuilder(
      this.schema,
      this.ordering,
      this.projections,
      this.mappings,
      subqueries,
      this.selector,
      this.project,
      this.restricted,
      this.filters
    );
  }
  filter(filter) {
    return new QueryBuilder(
      this.schema,
      this.ordering,
      this.projections,
      this.mappings,
      this.subqueries,
      this.selector,
      this.project,
      this.restricted,
      [...this.filters, filter]
    );
  }
  get option() {
    return [
      `_type == '${this.schema._type}'`,
      ...this.filters.map(
        (filter) => filter.includes("||") ? `(${filter})` : filter
      )
    ].join(" && ");
  }
  get order() {
    if (!this.ordering.length)
      return "";
    return ` | order(${this.ordering.map(([key, order]) => `${String(key)} ${order}`).join(", ")})`;
  }
  get projection() {
    const entries = Object.entries({
      ...this.projections,
      ...this.mappings,
      ...Object.keys(this.subqueries).reduce(
        (acc, key) => {
          acc[key] = this.subqueries[key][0];
          return acc;
        },
        {}
      )
    }).filter(([key]) => typeof key === "string");
    if (!this.project && entries.length === 1)
      return `.${entries[0][1]}`;
    if (!entries.length)
      return "";
    const innerProjection = [
      ...this.restricted ? [] : ["..."],
      ...entries.sort((a, b) => a[0].localeCompare(b[0])).map(([key, val]) => key === val ? key : `"${key}": ${val}`)
    ].join(", ");
    return ` { ${innerProjection} }`;
  }
  get query() {
    return `*[${this.option}]${this.order}${this.selector}${this.projection}`;
  }
  use() {
    return [
      this.query,
      this.selector === " [0]" ? null : []
    ];
  }
}

function defineFields(fields) {
  return Object.entries(fields).map(([key, value]) => ({
    title: splitStringByCase(key),
    ...value,
    name: key
  }));
}
function defineDocument(documentTitle, schema, _types) {
  return {
    /**
     * What is passed in, in case you need access to the underlying (typed) object.
     */
    schema,
    /**
     * Typed query builder for the document created
     */
    builder: new QueryBuilder({
      _type: documentTitle
    }),
    /**
     * Type for use with query builder
     */
    [documentTitle]: {
      _type: documentTitle
    },
    /**
     * Defined document that you can export as the schema type to be consumed by the Sanity CMS
     */
    document: {
      name: documentTitle,
      fields: defineFields(schema),
      title: splitStringByCase(documentTitle),
      type: "document"
    }
  };
}
function defineObject(objectTitle, schema, _types) {
  return {
    /**
     * What is passed in, in case you need access to the underlying (typed) object.
     */
    schema,
    /**
     * Type for use with query builder
     */
    [objectTitle]: {
      _type: objectTitle
    },
    /**
     * Defined object that you can export as the schema type to be consumed by the Sanity CMS
     */
    object: {
      name: objectTitle,
      fields: defineFields(schema),
      title: splitStringByCase(objectTitle),
      type: "object"
    }
  };
}

const type = Symbol("the type of the property");

const to = Symbol("type of object referred to by the reference");

export { QueryBuilder, defineDocument, defineFields, defineObject, to, type };
